import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { dismissalId } from "$global-providers/notifications-provider/deterministic-ids";
import { useLazyUser } from "$global-providers/user-provider";
import { gql } from "@apollo/client";
import type { NotificationDismissalInput } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { NotificationDismissal as RxNotificationDismissal } from "../collections/notification-dismissal";
import type { RxDBCollections } from "../provider";
import {
  SyncNotificationDismissalPullDocument,
  SyncNotificationDismissalPushDocument,
  type WholeSyncableNotificationDismissalFragment,
} from "./__generated__/notification-dismissal.gql";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicateNotificationDismissal = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxNotificationDismissal>;
}) => {
  const [flagEnabled] = useFeatureFlag<boolean>(
    featureFlags.notifications.center,
  );
  // Replication is per-user; shared-device installs need a user-scoped
  // checkpoint so a later user can't inherit a previous user's pull cursor
  // (see Copilot review on PR #2115).
  const user = useLazyUser();
  const userId = user?.id;
  const enabled = flagEnabled && !!userId;

  const pull = useMemo<
    | ReplicationPullConfig<WholeSyncableNotificationDismissalFragment>
    | undefined
  >(() => {
    if (!enabled) return undefined;
    return {
      query: SyncNotificationDismissalPullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { minutes: 5 },
    };
  }, [enabled]);

  const push = useMemo(
    () =>
      enabled && userId
        ? {
            mutation: SyncNotificationDismissalPushDocument,
            mapRxToGql: mapRxNotificationDismissalToGqlInput,
            // Per-user scoping on a shared device — see notification-bell-state.ts.
            filter: (doc: { userId: string }) => doc.userId === userId,
          }
        : undefined,
    [enabled, userId],
  );

  return useGqlReplication<
    RxNotificationDismissal,
    WholeSyncableNotificationDismissalFragment,
    NotificationDismissalInput
  >({
    db,
    collection,
    pull,
    push,
    mapGqlToRx: mapGqlNotificationDismissalToRx,
    batchSize: 100,
    metadataLocalStorageKeySuffix: `notification-dismissal-${userId ?? "anonymous"}`,
    replicationIdentifierSuffix: userId,
    enabled,
  });
};

export const mapGqlNotificationDismissalToRx = (
  doc: WholeSyncableNotificationDismissalFragment,
): WithDeleted<RxNotificationDismissal> => ({
  // Rewrite id deterministically so pulls from servers that recorded the row
  // under an older random uuid still resolve to the canonical local id and
  // dedupe with locally-emitted dismissals on upsert.
  id: dismissalId(doc.userId, doc.key, doc.versionAtDismissal ?? null),
  userId: doc.userId,
  key: doc.key,
  versionAtDismissal: doc.versionAtDismissal ?? null,
  dismissedAt: doc.dismissedAt,
  meta: {
    createdAt: doc.meta.createdAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedAt: doc.meta.updatedAt,
    updatedByUserId: doc.meta.updatedByUserId,
  },
  _deleted: doc.meta.deleted,
});

export const mapRxNotificationDismissalToGqlInput = (
  doc: WithDeleted<RxNotificationDismissal>,
): NotificationDismissalInput => ({
  id: doc.id,
  userId: doc.userId,
  key: doc.key,
  versionAtDismissal: doc.versionAtDismissal ?? null,
  dismissedAt: doc.dismissedAt,
  meta: {
    createdAt: doc.meta.createdAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedAt: doc.meta.updatedAt,
    updatedByUserId: doc.meta.updatedByUserId,
    deleted: doc._deleted,
  },
});

gql`
  fragment WholeSyncableNotificationDismissal on NotificationDismissal {
    id
    userId
    key
    versionAtDismissal
    dismissedAt
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncNotificationDismissalPull(
    $input: PullNotificationDismissalInput!
  ) {
    pullNotificationDismissal(input: $input) {
      documents {
        ...WholeSyncableNotificationDismissal
      }
    }
  }
`;

gql`
  mutation SyncNotificationDismissalPush(
    $input: PushNotificationDismissalInput!
  ) {
    pushNotificationDismissal(input: $input) {
      rows {
        ...WholeSyncableNotificationDismissal
      }
    }
  }
`;
